'use client';

import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	ReactNode,
} from 'react';
import type {
	Organization,
	UserOrganizationWithRole,
} from '../types/organization.type';
import { useQueryClient } from '@tanstack/react-query';
import {
	createOrganization as createOrganizationAction,
	getUserOrganizations as getUserOrganizationsAction,
	acceptOrganizationInvite as acceptOrganizationInviteAction,
	initSession as initSessionAction,
} from '../actions/organization-actions';
import { organizationInviteStorage } from '../utils/organization-invite-utils';
import { updateUserMetadata } from '@/features/auth';
import { safeFetchQuery, useSafeMutation } from '@/core/query';
import { useAuth } from '@/features/auth';
import type { AuthUser } from '@/features/auth';
import { useAnalytics } from '@/core/analytics';
import { logger } from '@/core/logger';
import {
	organizationStorage,
	organizationsStorage,
	currentOrgIdStorage,
	readCurrentOrganizationId,
} from '../utils/organization-storage';

interface IOrganizationsContext {
	// State
	organizations: UserOrganizationWithRole[];
	currentOrganization: UserOrganizationWithRole | null;
	currentRole: 'OWNER' | 'ADMIN' | 'MEMBER' | null;
	isLoading: boolean;
	error: string | null;

	// Actions
	refreshOrganizations: () => Promise<void>;
	createOrganization: (name: string) => Promise<Organization>;
	switchOrganization: (organizationId: string) => Promise<void>; // Currently unused - feature flag disabled
}

const OrganizationsContext = createContext<IOrganizationsContext | undefined>(
	undefined
);

export interface OrganizationsProviderProps {
	children: ReactNode;
}

export function OrganizationsProvider({
	children,
}: OrganizationsProviderProps) {
	const queryClient = useQueryClient();
	const { user, refreshUser, isLoading: isAuthLoading } = useAuth();
	const analytics = useAnalytics();

	const [organizations, setOrganizations] = useState<
		UserOrganizationWithRole[]
	>([]);
	const [currentOrganizationId, setCurrentOrganizationId] = useState<
		string | null
	>(() => currentOrgIdStorage.get());
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Mutation for creating organization
	const { mutateAsync: createOrganizationMutation } = useSafeMutation({
		action: (name: string) => createOrganizationAction(name),
	});

	// Mutation for updating user metadata
	const { mutateAsync: updateUserMetadataMutation } = useSafeMutation({
		action: (data: { current_organization_id: string }) =>
			updateUserMetadata(data),
	});

	// Derive current organization from tracked ID, falling back to first org
	const currentOrganization =
		organizations.find((o) => o.id === currentOrganizationId) ||
		organizations[0] ||
		null;
	const currentRole = currentOrganization?.role || null;

	// Define refreshOrganizations before it's used in useEffect
	const refreshOrganizations = useCallback(async (): Promise<void> => {
		if (!user?.id) {
			logger.warn('Cannot refresh organizations without user');
			return;
		}

		try {
			setError(null);

			// Ensure user record exists in backend (upsert)
			// This is idempotent and ensures getMembers() can resolve user details
			await initSessionAction();

			const fetchedOrganizations = await safeFetchQuery(queryClient, {
				queryKey: ['user-organizations'],
				action: () => getUserOrganizationsAction(),
			});

			// Store all organizations
			setOrganizations(fetchedOrganizations);

			if (fetchedOrganizations.length > 0) {
				// Cache all orgs
				organizationsStorage.save(fetchedOrganizations);

				// Set current org ID to first org only if not already set to a valid one
				setCurrentOrganizationId((prev) => {
					const isValid = fetchedOrganizations.some(
						(o) => o.id === prev
					);
					if (!isValid) {
						const defaultId = fetchedOrganizations[0].id;
						currentOrgIdStorage.save(defaultId);
						return defaultId;
					}
					return prev;
				});

				logger.info('Organizations loaded successfully', {
					count: fetchedOrganizations.length,
				});
			} else {
				logger.warn('User has no organizations');
				organizationsStorage.clear();
				currentOrgIdStorage.clear();
				organizationStorage.clear();
			}
		} catch (err) {
			logger.error('Failed to load organizations', err);
			setError('Failed to load organizations');

			// Use cached orgs if available (offline support)
			const cachedOrgs = organizationsStorage.get();
			if (cachedOrgs && cachedOrgs.length > 0) {
				logger.info('Using cached organizations after API failure');
				setOrganizations(cachedOrgs);
				analytics.logEvent('organization_loaded_from_cache');
			} else {
				setOrganizations([]);
			}
		} finally {
			setIsLoading(false);
		}
	}, [user?.id, analytics, queryClient]);

	// Auto-accept a pending invitation, then load organizations.
	// Handles the flow where a new user signs up via invite link and has no orgs yet.
	const acceptPendingInviteAndLoad = useCallback(
		async (inviteCode: string) => {
			try {
				logger.info('Auto-accepting pending organization invitation');
				const result = await acceptOrganizationInviteAction(inviteCode);

				if (result?.data?.organizationId) {
					await updateUserMetadataMutation({
						current_organization_id: result.data.organizationId,
					});
					await refreshUser();
					organizationInviteStorage.clear();
					logger.info('Pending invitation accepted successfully', {
						organizationId: result.data.organizationId,
					});
				} else {
					logger.warn(
						'Invitation acceptance returned no organization, clearing stored invite'
					);
					organizationInviteStorage.clear();
				}
			} catch (err) {
				logger.warn('Failed to auto-accept pending invitation', err);
				organizationInviteStorage.clear();
			}

			// Always load organizations after invite attempt (success or failure)
			await refreshOrganizations();
		},
		[refreshUser, refreshOrganizations, updateUserMetadataMutation]
	);

	// Load organizations on mount and when user changes
	useEffect(() => {
		// Wait until auth finishes resolving
		if (isAuthLoading) {
			logger.debug('Waiting for user authentication to complete');
			return;
		}

		// Auth is done but no user (expired/invalid session) — stop loading so
		// the middleware redirect to /login can take effect instead of infinite spinner.
		if (!user) {
			setIsLoading(false);
			return;
		}

		// Serve from cache immediately for fast paint, then always refresh in the
		// background so new org memberships and role changes are picked up.
		const cachedOrgs = organizationsStorage.get();
		if (cachedOrgs && cachedOrgs.length > 0) {
			setOrganizations(cachedOrgs);

			// Validate/initialize current org ID from cache
			setCurrentOrganizationId((prev) => {
				const isValid = cachedOrgs.some((o) => o.id === prev);
				if (!isValid) {
					const defaultId = cachedOrgs[0].id;
					currentOrgIdStorage.save(defaultId);
					return defaultId;
				}
				return prev;
			});

			setIsLoading(false);

			logger.info(
				'Serving organizations from cache, refreshing in background',
				{
					userId: user.id,
					count: cachedOrgs.length,
				}
			);

			// Background refresh — catches new memberships and role changes.
			// Don't await: cache already unblocked the render.
			refreshOrganizations().catch((err) => {
				logger.warn('Background org refresh failed', err);
			});
			return;
		}

		// Check for pending invitation — auto-accept before fetching organizations.
		// Without this, a new invited user has no orgs, OnboardingGuard redirects
		// to /organization/create, and the OrganizationInviteDialog never renders.
		const pendingInvite = organizationInviteStorage.get();
		if (pendingInvite?.inviteCode) {
			logger.info(
				'Found pending invitation, accepting before loading orgs',
				{
					userId: user.id,
				}
			);
			acceptPendingInviteAndLoad(pendingInvite.inviteCode);
			return;
		}

		// No cache, no invite — fetch from API
		logger.info('Cache miss or expired, fetching from API', {
			userId: user.id,
		});
		refreshOrganizations();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id, isAuthLoading]); // Re-run when auth resolves or user changes

	const createOrganization = useCallback(
		async (name: string): Promise<Organization> => {
			try {
				// Block if user already has an org (1 org per user for now)
				if (organizations.length > 0) {
					throw new Error('You already have an organization');
				}

				const newOrg = await createOrganizationMutation(name);

				analytics.logEvent('organization_created');

				// Immediately anchor local state to the new org so that
				// OnboardingGuard and subsequent navigation see it right away.
				// This must happen before refreshOrganizations() so the cache
				// write succeeds even if the API round-trip below is slow.
				const newOrgWithRole = {
					...newOrg,
					role: 'OWNER' as const,
				};
				setOrganizations([newOrgWithRole]);
				setCurrentOrganizationId(newOrg.id);
				organizationsStorage.save([newOrgWithRole]);
				currentOrgIdStorage.save(newOrg.id);

				// Update Supabase metadata so the refreshed JWT carries the org
				// ID on the next page load (mirrors what switchOrganization does).
				await updateUserMetadataMutation({
					current_organization_id: newOrg.id,
				});

				// Best-effort API sync — errors are swallowed so the UI can proceed
				refreshOrganizations().catch((err) => {
					logger.warn(
						'Background org refresh failed after creation',
						err
					);
				});

				return newOrg;
			} catch (error) {
				logger.error('Organization creation error', error);
				analytics.logError('organization_creation_failed');
				throw error;
			}
		},
		[
			analytics,
			refreshOrganizations,
			organizations,
			createOrganizationMutation,
			updateUserMetadataMutation,
		]
	);

	const switchOrganization = useCallback(
		async (organizationId: string): Promise<void> => {
			try {
				// Update local state immediately so UI reflects the switch
				setCurrentOrganizationId(organizationId);
				currentOrgIdStorage.save(organizationId);

				// Update current organization in user metadata and refresh JWT on server
				await updateUserMetadataMutation({
					current_organization_id: organizationId,
				});

				// Refresh user state to get updated JWT with new organization
				await refreshUser();

				// Wipe React Query cache so any stale per-org data is dropped before
				// the caller triggers a navigation/reload.
				queryClient.clear();

				// Invalidate cached orgs so next load hits the API fresh
				organizationsStorage.clear();

				analytics.logEvent('organization_switched');
			} catch (error) {
				logger.error('Organization switch error', error);
				analytics.logError('organization_switch_failed');
				throw new Error('Failed to switch organization');
			}
		},
		[analytics, refreshUser, updateUserMetadataMutation, queryClient]
	);

	const value: IOrganizationsContext = {
		organizations,
		currentOrganization,
		currentRole,
		isLoading,
		error,
		refreshOrganizations,
		createOrganization,
		switchOrganization,
	};

	return (
		<OrganizationsContext.Provider value={value}>
			{children}
		</OrganizationsContext.Provider>
	);
}

/**
 * The organization to BILL a request against — the one the UI is showing.
 *
 * Reads the provider rather than localStorage, deliberately. The two can
 * disagree: `currentOrganization` falls back to a default when the stored id is
 * not in the user's list, and another tab can change the stored id without this
 * tab's state following. Billing the stored id in either case charges an
 * organization other than the one on screen, which is the same class of bug as
 * the arbitrary-org fallback this exists to prevent (TSO-810).
 *
 * Falls back to storage only when there is no provider at all, so the shared
 * Enhance control stays usable outside the dashboard layout instead of throwing.
 * Returns undefined when neither knows, in which case the backend applies its
 * own fallback — correct for a single-org user, and the only case left.
 */
export function useBillingOrganizationId(): string | undefined {
	const context = useContext(OrganizationsContext);
	return context?.currentOrganization?.id ?? readCurrentOrganizationId();
}

export function useOrganizations(): IOrganizationsContext {
	const context = useContext(OrganizationsContext);

	if (!context) {
		throw new Error(
			'useOrganizations must be used within an OrganizationsProvider'
		);
	}

	return context;
}
